/**
 * COL-417: record money received against a rent month.
 *
 * Two steps on purpose. The ticket asks for a confirmation gate "to reduce
 * fat-finger risk", and the risk here is a mistyped AMOUNT, not a mis-click —
 * so the gate does not ask "are you sure?", it restates the arithmetic the
 * entry produces: what was owed, what had already arrived, what this adds, and
 * what will still be open afterwards. A wrong figure is visible in that
 * summary in a way it is not in an input box.
 *
 * The rent/fee split exists because Mahngebühren are not part of the month's
 * `amount_cents`. A tenant owing EUR 450 rent plus EUR 20 of accrued fees sends
 * EUR 470; without the split that reads as a EUR 20 overpayment. The ADMIN
 * states the split — nothing here infers whether rent or fees are paid first,
 * which is the question COL-417 explicitly defers.
 *
 * Only the fee portion is editable, with rent derived as the remainder. Two
 * free-floating boxes that must sum to a third number is a way to record EUR 30
 * of a EUR 470 transfer by accident; one box cannot be inconsistent.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRecordPayment } from "@/hooks/v2/usePaymentReceipts";

const METHODS = ["bank_transfer", "cash", "card", "sepa", "other"] as const;

function eur(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

/** Today in the browser's timezone, as the ISO date an <input type="date"> wants. */
function todayIso(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
}

export function RecordPaymentDialog({
  open,
  onOpenChange,
  payment,
  monthLabel,
  accruedFeesEur,
  onRecorded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The rent row being paid toward. */
  payment: {
    id: string;
    amount_cents?: number | null;
    received_cents?: number | null;
  } | null;
  /** e.g. "September 2026" — so the dialog names the month it will change. */
  monthLabel: string;
  /** bookings.accumulated_fees_eur, to pre-fill and bound the fee portion. */
  accruedFeesEur: number;
  onRecorded?: () => void;
}) {
  const { t } = useLanguage();
  const record = useRecordPayment();

  const [step, setStep] = useState<"form" | "confirm">("form");
  const [amount, setAmount] = useState("");
  const [feeAmount, setFeeAmount] = useState("0");
  const [receivedAt, setReceivedAt] = useState(todayIso());
  const [method, setMethod] = useState<string>("bank_transfer");
  const [note, setNote] = useState("");

  // A reopened dialog must never inherit the last entry — re-recording the
  // previous amount against a different month is exactly the mistake the
  // confirmation gate exists to prevent, so it must not be one keystroke away.
  useEffect(() => {
    if (!open) return;
    setStep("form");
    setAmount("");
    setFeeAmount("0");
    setReceivedAt(todayIso());
    setMethod("bank_transfer");
    setNote("");
  }, [open]);

  const expectedCents = Math.max(
    0,
    Math.round(Number(payment?.amount_cents ?? 0))
  );
  const alreadyCents = Math.max(
    0,
    Math.round(Number(payment?.received_cents ?? 0))
  );
  const openBeforeCents = Math.max(0, expectedCents - alreadyCents);
  const accruedFeeCents = Math.max(
    0,
    Math.round((Number(accruedFeesEur) || 0) * 100)
  );

  const parsed = useMemo(() => {
    const total = Math.round((parseFloat(amount.replace(",", ".")) || 0) * 100);
    const fee = Math.round(
      (parseFloat(feeAmount.replace(",", ".")) || 0) * 100
    );
    const boundedFee = Math.max(0, Math.min(fee, Math.max(0, total)));
    return {
      totalCents: total,
      feeCents: boundedFee,
      rentCents: Math.max(0, total - boundedFee),
    };
  }, [amount, feeAmount]);

  const openAfterCents = Math.max(0, openBeforeCents - parsed.rentCents);
  const overpaidCents = Math.max(0, parsed.rentCents - openBeforeCents);
  const settles = parsed.rentCents >= openBeforeCents && openBeforeCents > 0;

  const validationError = (): string | null => {
    if (!payment)
      return t("admin.recordPayment.noRow", "No payment row selected.");
    if (!Number.isFinite(parsed.totalCents) || parsed.totalCents <= 0)
      return t(
        "admin.recordPayment.amountRequired",
        "Enter an amount greater than €0."
      );
    if (!/^\d{4}-\d{2}-\d{2}$/.test(receivedAt))
      return t(
        "admin.recordPayment.dateRequired",
        "Pick the date the money arrived."
      );
    return null;
  };

  const goToConfirm = () => {
    const err = validationError();
    if (err) {
      toast.error(err);
      return;
    }
    setStep("confirm");
  };

  const submit = async () => {
    const err = validationError();
    if (err || !payment) {
      toast.error(err ?? "");
      return;
    }
    try {
      await record.mutateAsync({
        paymentId: payment.id,
        rentCents: parsed.rentCents,
        feeCents: parsed.feeCents,
        receivedAt: new Date(`${receivedAt}T12:00:00`).toISOString(),
        method,
        note: note.trim() || null,
      });
      toast.success(
        settles
          ? t(
              "admin.recordPayment.toastSettled",
              "Payment recorded — month settled."
            )
          : t("admin.recordPayment.toastPartial", "Partial payment recorded.")
      );
      onRecorded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t(
              "admin.recordPayment.toastFailed",
              "Could not record the payment."
            )
      );
    }
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: "#94A3B8",
    marginBottom: 5,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>
            {t("admin.recordPayment.title", "Partial Payment")}
          </DialogTitle>
          <DialogDescription>
            {monthLabel} · {t("admin.recordPayment.openNow", "open")}{" "}
            {eur(openBeforeCents)}
            {alreadyCents > 0 && (
              <>
                {" · "}
                {eur(alreadyCents)}{" "}
                {t("admin.recordPayment.alreadyReceived", "already received")}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {step === "form" ? (
          <div style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              <div>
                <div style={labelStyle}>
                  {t("admin.recordPayment.amount", "Amount received (€)")}
                </div>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={(openBeforeCents / 100).toFixed(2)}
                  autoFocus
                />
              </div>
              <div>
                <div style={labelStyle}>
                  {t("admin.recordPayment.receivedOn", "Received on")}
                </div>
                <Input
                  type="date"
                  value={receivedAt}
                  onChange={(e) => setReceivedAt(e.target.value)}
                />
              </div>
            </div>

            <div>
              <div style={labelStyle}>
                {t("admin.recordPayment.method", "Method")}
              </div>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                style={{
                  width: "100%",
                  height: 40,
                  borderRadius: 8,
                  border: "1px solid #CBD5E1",
                  padding: "0 10px",
                  fontSize: 14,
                  background: "#fff",
                }}
              >
                {METHODS.map((m) => (
                  <option key={m} value={m}>
                    {t(`admin.recordPayment.method.${m}`, m.replace(/_/g, " "))}
                  </option>
                ))}
              </select>
            </div>

            {/* Only offered when fees actually exist — an admin should not be
                invited to split a payment against a balance of zero. */}
            {accruedFeeCents > 0 && (
              <div
                style={{
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  padding: "10px 12px",
                }}
              >
                <div style={labelStyle}>
                  {t(
                    "admin.recordPayment.feePortion",
                    "Of which Mahngebühren (€)"
                  )}
                </div>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={feeAmount}
                  onChange={(e) => setFeeAmount(e.target.value)}
                />
                <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 6 }}>
                  {eur(accruedFeeCents)}{" "}
                  {t("admin.recordPayment.feesAccrued", "accrued")} ·{" "}
                  {t(
                    "admin.recordPayment.feeSplitHint",
                    "the rest counts toward the rent"
                  )}
                </div>
              </div>
            )}

            <div>
              <div style={labelStyle}>
                {t("admin.recordPayment.note", "Note (optional)")}
              </div>
              <Input value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
        ) : (
          <div
            style={{
              background: settles ? "#E7F7EF" : "#FEF6E7",
              border: `1px solid ${settles ? "#BBEBD5" : "#F7E4BD"}`,
              borderRadius: 8,
              padding: "12px 13px",
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: settles ? "#065F46" : "#7C4A0B",
                marginBottom: 8,
              }}
            >
              {settles
                ? t("admin.recordPayment.willSettle", "This settles the month.")
                : t(
                    "admin.recordPayment.willRemainOpen",
                    "This is less than the full amount. Check the figures."
                  )}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: settles ? "#065F46" : "#92600D",
                lineHeight: 1.9,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <Line
                label={t("admin.recordPayment.billed", "Billed")}
                value={eur(expectedCents)}
              />
              <Line
                label={t("admin.recordPayment.alreadyIn", "Already received")}
                value={eur(alreadyCents)}
              />
              <Line
                label={t("admin.recordPayment.thisRent", "This payment — rent")}
                value={eur(parsed.rentCents)}
              />
              {parsed.feeCents > 0 && (
                <Line
                  label={t(
                    "admin.recordPayment.thisFee",
                    "This payment — Mahngebühren"
                  )}
                  value={eur(parsed.feeCents)}
                />
              )}
              <Line
                label={t(
                  "admin.recordPayment.stillOpen",
                  "Still open after this"
                )}
                value={eur(openAfterCents)}
                strong
              />
              {overpaidCents > 0 && (
                <div style={{ marginTop: 6, fontWeight: 700 }}>
                  {t(
                    "admin.recordPayment.overpaid",
                    "Overpayment carried forward as credit"
                  )}
                  : {eur(overpaidCents)}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "form" ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("admin.recordPayment.cancel", "Cancel")}
              </Button>
              <Button onClick={goToConfirm}>
                {t("admin.recordPayment.review", "Review")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep("form")}>
                {t("admin.recordPayment.back", "Back")}
              </Button>
              <Button onClick={submit} disabled={record.isPending}>
                {record.isPending
                  ? t("admin.recordPayment.recording", "Recording…")
                  : `${t("admin.recordPayment.record", "Record")} ${eur(parsed.totalCents)}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Line({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        fontWeight: strong ? 700 : 400,
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
